/**
 * TripMaster-only browser document titles (tab + SEO <title>).
 * HotelsRiver keeps its own SeoMetaGuard / package SEO paths.
 */

export const TRIPMASTER_BRAND_NAME = "TripMaster";
export const TRIPMASTER_TITLE_SEP = " · ";
export const TRIPMASTER_ALL_DESTINATIONS_LABEL = "כל היעדים";

function str(value) {
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
}

/**
 * Join non-empty title segments for a single-line browser tab.
 * @param {Array<string|null|undefined>} parts
 * @returns {string}
 */
export function joinTripMasterTitleParts(parts) {
  const out = [];
  for (const part of parts || []) {
    const text = str(part);
    if (text) out.push(text);
  }
  return out.join(TRIPMASTER_TITLE_SEP);
}

/**
 * Normalize destination label for titles: first segment, or "כל היעדים".
 * Collapses "כל היעדים, כל היעדים" → "כל היעדים".
 * @param {string|null|undefined} destinationLine
 * @param {{ fallbackAllDestinations?: boolean }} [opts]
 * @returns {string|null}
 */
export function resolveTripMasterDestinationLabel(destinationLine, opts = {}) {
  const fallbackAll = opts.fallbackAllDestinations !== false;
  const raw = str(destinationLine);
  if (!raw) return fallbackAll ? TRIPMASTER_ALL_DESTINATIONS_LABEL : null;

  const unique = [
    ...new Set(
      raw
        .split(/[,/]/)
        .map((part) => part.trim())
        .filter(Boolean)
    ),
  ];
  if (unique.length === 1) return unique[0];
  if (unique.length > 1 && unique.every((part) => part === unique[0])) return unique[0];

  const first = raw.split(/[/,·]/)[0].trim();
  if (!first) return fallbackAll ? TRIPMASTER_ALL_DESTINATIONS_LABEL : null;
  return first;
}

/**
 * Prefer en-dash in date ranges for readable Hebrew titles.
 * @param {string|null|undefined} datesLine
 * @returns {string|null}
 */
export function normalizeTripMasterDatesLine(datesLine) {
  const raw = str(datesLine);
  if (!raw) return null;
  return raw.replace(/\s*-\s*/g, " – ");
}

/**
 * Search / results tab title:
 * כל היעדים · 18/10/2026 – 22/10/2026 · 2 מבוגרים · 4 לילות
 */
export function buildTripMasterSearchResultsTitle({
  destinationLabel,
  datesLine,
  guestsLine,
  nightsLine,
} = {}) {
  return joinTripMasterTitleParts([
    resolveTripMasterDestinationLabel(destinationLabel),
    normalizeTripMasterDatesLine(datesLine),
    str(guestsLine),
    str(nightsLine),
  ]);
}

/**
 * Package view tab title: hotel · stay dates (± destination).
 */
export function buildTripMasterPackageTitle({
  hotelName,
  datesLine,
  destinationLabel,
} = {}) {
  return joinTripMasterTitleParts([
    str(hotelName) || "חבילת נופש",
    normalizeTripMasterDatesLine(datesLine),
    resolveTripMasterDestinationLabel(destinationLabel, { fallbackAllDestinations: false }),
  ]);
}

export function buildTripMasterHomeTitle(brandName = TRIPMASTER_BRAND_NAME) {
  return joinTripMasterTitleParts([brandName, "חבילות נופש"]);
}

export function buildTripMasterCategoryTitle(category, brandName = TRIPMASTER_BRAND_NAME) {
  const key = String(category || "").toLowerCase();
  if (key === "hotels") return joinTripMasterTitleParts([brandName, "מלונות"]);
  if (key === "flights") return joinTripMasterTitleParts([brandName, "טיסות"]);
  if (key === "packages") return joinTripMasterTitleParts([brandName, "חבילות נופש"]);
  return buildTripMasterHomeTitle(brandName);
}

export function buildTripMasterDestinationPageTitle(destinationLabel, brandName = TRIPMASTER_BRAND_NAME) {
  const dest = resolveTripMasterDestinationLabel(destinationLabel, {
    fallbackAllDestinations: false,
  });
  if (dest) return joinTripMasterTitleParts([dest, "חבילות נופש", brandName]);
  return buildTripMasterHomeTitle(brandName);
}

export function buildTripMasterStaticPageTitle(pageLabel, brandName = TRIPMASTER_BRAND_NAME) {
  const label = str(pageLabel);
  if (!label) return buildTripMasterHomeTitle(brandName);
  if (label === brandName) return brandName;
  return joinTripMasterTitleParts([label, brandName]);
}

/**
 * SSOT resolver for VacationPackagesPublicFlow on the TripMaster surface.
 */
export function resolveTripMasterPublicFlowDocumentTitle({
  packageRouteId = null,
  stage = null,
  isDedicatedDestinationRoute = false,
  dedicatedDestinationPageTitle = null,
  showPublicHomeHero = false,
  activeAgentCategory = "home",
  destinationLine = null,
  datesLine = null,
  guestsLine = null,
  nightsLine = null,
  packageHotelName = null,
  packageDatesLine = null,
  packageDestinationLabel = null,
  brandName = TRIPMASTER_BRAND_NAME,
} = {}) {
  const isPackageRoute = Boolean(str(packageRouteId));
  if (isPackageRoute) {
    const hotel = str(packageHotelName);
    const dates = normalizeTripMasterDatesLine(packageDatesLine || datesLine);
    if (stage === "booking") {
      return joinTripMasterTitleParts([hotel || "חבילת נופש", "הזמנה", dates]);
    }
    if (hotel || dates) {
      return buildTripMasterPackageTitle({
        hotelName: hotel,
        datesLine: dates,
        destinationLabel: packageDestinationLabel || destinationLine,
      });
    }
    return joinTripMasterTitleParts(["חבילת נופש", brandName]);
  }

  if (isDedicatedDestinationRoute) {
    return (
      str(dedicatedDestinationPageTitle) ||
      buildTripMasterDestinationPageTitle(destinationLine, brandName)
    );
  }

  const hasStayContext = Boolean(str(datesLine) || str(nightsLine) || str(guestsLine));
  const onResults =
    stage === "results" ||
    (hasStayContext && !showPublicHomeHero) ||
    (hasStayContext && stage === "search" && Boolean(str(datesLine)));

  if (onResults) {
    return buildTripMasterSearchResultsTitle({
      destinationLabel: destinationLine,
      datesLine,
      guestsLine,
      nightsLine,
    });
  }

  if (stage === "booking") {
    return joinTripMasterTitleParts(["הזמנה", brandName]);
  }

  if (stage === "outcome") {
    return joinTripMasterTitleParts(["אישור הזמנה", brandName]);
  }

  if (showPublicHomeHero || stage === "search" || !stage) {
    return buildTripMasterCategoryTitle(activeAgentCategory, brandName);
  }

  return buildTripMasterHomeTitle(brandName);
}
